import {
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";

import type { Database, Json } from "../../../__definitions__/database-definitions";
import type { UUID } from "../../../utils/uuid";

export type PersonWithManager = Database["public"]["Views"]["person_csv_data"]["Row"];
export type Permissions = Database["public"]["Views"]["permissions"]["Row"];
export type PersonDetails = Database["public"]["Views"]["person_details"]["Row"];
export type EmployeeDataRow =
  Database["public"]["Functions"]["employee_data_page"]["Returns"][number];
export interface EmployeeDataPageCursor {
  id: UUID;
  value: string | null;
}
export interface EmployeeDataFilter {
  items: { field: string; operator: string; value?: Json }[];
  logicOperator?: "and" | "or";
}
export interface EmployeeDataPageQuery {
  cursor: EmployeeDataPageCursor | null;
  pageSize: number;
  segmentId: UUID | null;
  sort: { field: string; direction: "asc" | "desc" };
  filter: EmployeeDataFilter;
}
export interface EmployeeDataGridPage {
  rows: (EmployeeDataRow["data"] & { id: UUID })[];
  rowCount: number;
  nextCursor: EmployeeDataPageCursor | null;
}

export function toEmployeeDataGridPage(data: EmployeeDataRow[] | null): EmployeeDataGridPage {
  const lastRow = data?.at(-1);
  return {
    rows: (data ?? []).map(({ data: row, id }) => ({ ...row, id })),
    rowCount: data?.[0]?.total_count ?? 0,
    nextCursor: lastRow ? { id: lastRow.id, value: lastRow.cursor_value } : null,
  };
}

export const personLabel = (person: PersonDetails): string =>
  `${person.first_name} ${person.last_name} (${person.email})`;

export class SupabasePersonService extends SupabaseService<"person"> {
  table = "person" as const;

  async getAllWithManager(account_id: UUID) {
    const select = this.client.from("person_csv_data").select("*");
    return this.getAllFromSelection(account_id, select, { order: [{ column: "id" }] });
  }

  async getEmployeeDataPage(account_id: UUID, query: EmployeeDataPageQuery) {
    return await this.client.rpc("employee_data_page", {
      account_id_: account_id,
      after_id_: query.cursor?.id,
      after_value_: query.cursor?.value ?? undefined,
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      filter_model_: query.filter as unknown as Json,
      limit_: query.pageSize,
      segment_id_: query.segmentId ?? undefined,
      sort_direction_: query.sort.direction,
      sort_field_: query.sort.field,
    });
  }

  async getEmployeeDataExportPage(
    account_id: UUID,
    after_id: UUID | null,
    limit: number,
    segment_id?: UUID | null,
  ) {
    return await this.client.rpc("employee_data_export_page", {
      account_id_: account_id,
      after_id_: after_id ?? undefined,
      limit_: limit,
      segment_id_: segment_id ?? undefined,
    });
  }

  async getEmployeeDataColumns(account_id: UUID) {
    return await this.client.rpc("employee_data_columns", { account_id_: account_id });
  }

  async getPeople(account_id: UUID) {
    const select = this.client.from("people").select("*");
    return this.getAllFromSelection(account_id, select, { order: [{ column: "name" }] });
  }

  async getWithManagerById(id: UUID) {
    return await this.client.from("person_csv_data").select("*").eq("id", id).single();
  }

  async getUniqueValues(account_id: UUID, key: string) {
    const select = this.client.from("unique_person_text_values").select("*").eq("key", key);
    return await this.getAllFromSelection(account_id, select, { order: [{ column: "key" }] });
  }

  async getUniqueValuesByAudience(account_id: UUID, key: string, audience: string) {
    const select = this.client
      .from("unique_recipient_person_text_values")
      .select("*")
      .eq("audience", audience)
      .eq("key", key);
    return await this.getAllFromSelection(account_id, select, { order: [{ column: "key" }] });
  }
  async getUniqueValuesForCountryType(account_id: UUID, key: string) {
    const select = this.client
      .from(`unique_person_country_values`)
      .select("*")
      .or(`key.eq.${key},key.is.null`);
    return await this.getAllFromSelection(account_id, select, { order: [{ column: "key" }] });
  }
}

export class SupabasePermissionsService extends SupabaseServiceView<"permissions"> {
  table = "permissions" as const;

  async setSuperAdmins(person_ids: UUID[]) {
    return this.client.rpc("set_super_admins", { person_ids });
  }

  async setReviewers(person_ids: UUID[]) {
    return this.client.rpc("set_reviewers", { person_ids });
  }

  async setPermissions(person_ids: UUID[], permissions: { [k: string]: UUID[] }) {
    return this.client.rpc("set_permissions", {
      person_ids,
      permissions_: permissions,
    });
  }

  async removePermissions(person_id: UUID) {
    return this.client.rpc("remove_all_permissions", { person_id });
  }

  async getCurrentUserPermissions() {
    return this.client.rpc("current_user_permissions");
  }
}

export class SupabasePersonDetailsService extends SupabaseServiceView<"person_details"> {
  table = "person_details" as const;
}
